/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { isBlank, NoResults, useAsyncEffect } from "@cd2/components"
import {
  Button,
  Grid,
  Icon,
  IconButton,
  List,
  Modal,
  Placeholder,
  Spacer,
  Table,
  TextGroupMedium,
} from "@cd2/components/build/es6"
import { Fields, Form2 } from "@cd2/forms"
import { useFormSubmitLock } from "@cd2/forms/build/es6/Form2"
import { H4, P } from "@cd2/text"
import { assert } from "@cd2/tools/build/es6"
import { differenceInSeconds, formatDistanceToNowStrict, sub } from "date-fns"
import React, { useState } from "react"
import { Link } from "react-router-dom"
import { CancelButton } from "../../Components/Buttons/CancelButton"
import { ConfirmCancelButtons } from "../../Components/Buttons/ConfirmCancelButtons"
import { CreateButton } from "../../Components/Buttons/CreateButton"
import { ButtonsWrapper } from "../../Components/ButtonsWrapper"
import { ConfirmCancelDialogue } from "../../Components/ConfirmCancelDialogue"
import { DropdownMenu } from "../../Components/DropdownMenu"
import { brandColors, themeColors } from "../../Components/EOSThemeProvider"
import { FieldWithDescription } from "../../Components/FieldWithDescription"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import { QuickToolTip } from "../../Components/QuickTooltip"
import { QuickToolTipWrapper } from "../../Components/QuickToolTipWrapper"
import { UserSelectList } from "../../Components/SelectLists/UserSelectList"
import { MenuColumn } from "../../Components/Tables/utils"
import { ToggleSwitch } from "../../Components/ToggleSwitch"
import { icons } from "../../constants/icons"
import { useBlockUserToggle } from "../../hooks/useBlockUserToggle"
import { useConfirmation } from "../../hooks/useConfirmation"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { buildRoute, eRoutes } from "../../routes"
import { IRecord } from "../../types/schema"
import {
  useEditUserConfirmation,
} from "../Customers/CustomerShowContactsPage"
import { ISubCustomerShowRecord } from "./SubCustomerShowPage"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"
import { useEvents } from "../../Components/EventsList"

type ICustomer = IRecord<"customers", "name" | "isGenericCustomer"> & {
  contacts: (IRecord<
    "contacts",
    | "job_title"
    | "name"
    | "first_name"
    | "last_name"
    | "email"
    | "phone"
    | "sage_id"
    | "receivesMonthlyPriceList"
    | "default_delivery_note"
    | "is_default"
    | "useForMsEmailEventFetch"
    | "marketingAccepted"
  > & {
    user: IRecord<
      "skeem_users",
      | "name"
      | "skeem_email"
      | "last_seen"
      | "isInvited"
      | "isBlocked"
      | "customerIsPrimaryAccountHolder"
      | "phoneNumber"
      | "marketingAccepted"
      | "customerAreaQuotesOnly"
    >
    quotes: IRecord<"quotes", "created_at">[]
    orders: IRecord<"orders", "created_at">[]
  })[]
}

export function SubCustomerShowContactsPage({
  subCustomer,
  refetch,
}: {
  subCustomer: ISubCustomerShowRecord
  refetch: () => Promise<void>
}) {
  const skeem = window.skeem
  const events = useEvents()
  const alerts = useSiteAlerts()
  const [contactQuery, setContactQuery] = React.useState<string>("")
  const [contactFormNode, getContactInformation, handleCreate] =
    useContactCreateForSubOrCustomerConfirmation()
  const [passwordNode, getPassword] = useEditUserConfirmation()
  const [addExistingContactsNode, handleAddExistingContacts] = useManageExistingContact({
    subCustomerId: subCustomer.id,
    customerId: subCustomer.customer.id,
    callback: refetch,
  })

  const [confirmDeleteUserNode, handleConfirmDelete] = useConfirmation(({ onResolve }) => (
    <ConfirmCancelDialogue
      heading={"Are you sure you want to delete this user?"}
      body={
        <React.Fragment>
          <P>This cannot be undone</P>
        </React.Fragment>
      }
      onConfirm={() => onResolve(true)}
      onCancel={() => onResolve(false)}
      buttonOpts={{ confirm: { children: "Delete", buttonColor: brandColors.blaze } }}
      isOpen
    />
  ))

  const [confirmBlockNode, getConfirmBlockNode] = useBlockUserToggle()

  async function handleCreateContact() {
    assert(subCustomer)
    const subCustomerRecord = await skeem.fetch2<
      IRecord<"sub_customers", "name"> & {
        customer: IRecord<"customers", "name" | "isGenericCustomer"> & {
          branch: IRecord<"branches">
        }
      }
    >({
      first: {
        name: "sub_customers",
        attributes: [
          "name",
          { name: "customer", attributes: ["name", "branch", "isGenericCustomer"] },
        ],
        filter: { matches: { id: subCustomer.id } },
      },
    })
    try {
      const createdContact = await handleCreate(subCustomerRecord.customer, subCustomerRecord)
      if (createdContact) {
        events.unassociatedWithInverse(
          "sub_customers",
          subCustomer.id,
          {
            association: "contacts",
            recordId: createdContact.id,
            nameAttr: "email",
          },
          "name",
        )
      }
      refetch()
    } catch (e) {
      console.error(e)
    }
  }

  async function handleEditContact(
    contact: ICustomer["contacts"][0],
    opts?: { focusUserSection?: boolean },
  ) {
    assert(subCustomer)
    const subCustomerRecord = await skeem.fetch2<
      IRecord<"sub_customers", "name"> & {
        customer: IRecord<"customers", "name" | "isGenericCustomer"> & {
          branch: IRecord<"branches">
        }
      }
    >({
      first: {
        name: "sub_customers",
        attributes: [
          "name",
          { name: "customer", attributes: ["name", "branch", "isGenericCustomer"] },
        ],
        filter: { matches: { id: subCustomer.id } },
      },
    })
    const contactData = await getContactInformation({
      contact,
      customer: subCustomerRecord.customer,
      subCustomer: subCustomerRecord,
      opts,
    })
    if (contactData === false) {
      return
    } else {
      try {
        const { createUser, emailOverrideField, password, quickInvite, ...mutationData } =
          contactData
        if (quickInvite) {
          try {
            const result = await skeem.mutate<{ user: { email: string } }>({
              contacts: {
                update: {
                  id: contact.id,
                  useForMsEmailEventFetch: mutationData.useForMsEmailEventFetch,
                  marketingAccepted: mutationData.marketingAccepted,
                  job_title: mutationData.job_title,
                  user: {
                    create: {
                      first_name: mutationData.first_name,
                      last_name: mutationData.last_name,
                      name:
                        mutationData.first_name || mutationData.last_name
                          ? mutationData.first_name + " " + mutationData.last_name
                          : mutationData.name,
                      skeem_email: emailOverrideField ?? mutationData.email,
                      userAccountType: "external",
                      customer: { set: subCustomerRecord.customer.id },

                      sub_customers: { add: subCustomerRecord.id },
                      branch: { set: subCustomerRecord.customer.branch.id },

                      skeem_password: generatePassword(),
                      phoneNumber: mutationData.phone,
                      marketingAccepted: mutationData.marketingAccepted,
                    },
                  },
                },
              },
            })
            await skeem.generatePasswordResetToken({ email: result.user.email })
            alerts.genericSuccess("Invite sent")
          } catch (error) {
            if (error.message.includes("Error mutating skeem_email: must be unique")) {
              alerts.genericError("Cannot create user account, email already in use.")
            }
            throw error
          }
        } else {
          await skeem.mutate({
            contacts: {
              update: {
                id: contact.id,
                ...mutationData,
                name:
                  mutationData.first_name || mutationData.last_name
                    ? mutationData.first_name + " " + mutationData.last_name
                    : mutationData.name,
                user: createUser
                  ? {
                      create: {
                        first_name: mutationData.first_name,
                        last_name: mutationData.last_name,
                        name:
                          mutationData.first_name || mutationData.last_name
                            ? mutationData.first_name + " " + mutationData.last_name
                            : mutationData.name,
                        skeem_email: emailOverrideField ?? mutationData.email,
                        skeem_password: generatePassword(),
                        userAccountType: "external",
                        customer: { set: subCustomerRecord.customer.id },

                        sub_customers: { add: subCustomerRecord.id },
                        branch: { set: subCustomerRecord.customer.branch.id },

                        phoneNumber: mutationData.phone,
                        marketingAccepted: mutationData.marketingAccepted,
                      },
                    }
                  : { set: contactData.user?.id || null },
              },
            },
          })
          if (mutationData.default_delivery_note) {
            const contacts = await skeem.fetch2<IRecord<"contacts">[]>({
              model: {
                name: "contacts",
                filter: {
                  and: [
                    { not: { matches: { id: contact.id } } },
                    {
                      matches: {
                        customer: subCustomerRecord.customer.id,

                        branch: subCustomerRecord.customer.branch.id,

                        default_delivery_note: true,
                      },
                    },
                  ],
                },
              },
            })
            await skeem.mutate({
              contacts: contacts.map(v => ({
                update: { id: v.id, default_delivery_note: false },
              })),
            })
          }
        }
        await refetch()
        alerts.genericSuccess("Contact updated successfully")
      } catch (e) {
        console.error(e)
        if (e.message.includes("must be unique")) {
          alerts.genericError("Failed to update contact, email already in use.")
        } else {
          alerts.genericError("Failed to update contact: " + e.message)
        }
      }
    }
  }

  async function handleChangeUserPassword(contact: ICustomer["contacts"][0]) {
    const passwordData = await getPassword({})
    if (passwordData === false) {
      return
    } else {
      try {
        await skeem.mutate({
          contacts: {
            update: {
              id: contact.id,
              user: {
                update: {
                  id: contact.user.id,
                  skeem_password: passwordData.password,
                },
              },
            },
          },
        })
        alerts.genericSuccess("Password updated successfully")
      } catch (e) {
        console.error(e)
        alerts.genericError("Failed to update password, please try again")
      }
    }
  }

  async function blockContact(contact: ICustomer["contacts"][0]) {
    const confirmBlock = await getConfirmBlockNode({ contact, block: !contact.user.isBlocked })
    if (!confirmBlock) {
      return
    } else {
      try {
        await skeem.mutate({
          contacts: {
            update: {
              id: contact.id,
              user: {
                update: { id: contact.user.id, isBlocked: !contact.user.isBlocked },
              },
            },
          },
        })
        alerts.genericSuccess(`Contact ${contact.user.isBlocked ? "unblocked" : "blocked"}`)
        refetch()
      } catch (e) {
        console.error(e)
        alerts.genericError(
          `Failed to ${contact.user.isBlocked ? "unblock" : "block"} contact, please try again`,
        )
      }
    }
  }

  async function handleToggleUseForMsResources(contactId: string, value: boolean) {
    await skeem.mutate({
      contacts: { update: { id: contactId, useForMsEmailEventFetch: value } },
    })
    refetch()
  }

  async function handleToggleCustomerAreaQuotesOnly(skeemUserId: string, value: boolean) {
    await skeem.mutate({
      skeem_users: { update: { id: skeemUserId, customerAreaQuotesOnly: value } },
    })
    refetch()
  }

  async function handleToggleMarketingAccepted(contactId: string, value: boolean) {
    assert(subCustomer, "Customer must be defined")

    await skeem.mutate({
      contacts: { update: { id: contactId, marketingAccepted: value } },
    })
    refetch()
  }

  async function handleDeleteUserAccount(id: string) {
    const shouldDelete = await handleConfirmDelete()
    if (shouldDelete) {
      await skeem.mutate({ skeem_users: { destroy: id } })
      await refetch()
      alerts.genericSuccess("User account deleted")
    }
  }

  async function handleRemove(id: string) {
    await skeem.mutate({ contacts: { update: { id, sub_customers: { remove: subCustomer.id } } } })
    events.unassociatedWithInverse(
      "sub_customers",
      subCustomer.id,
      {
        association: "contacts",
        recordId: id,
        nameAttr: "email",
      },
      "name",
    )
    await refetch()
    alerts.genericSuccess("Contact removed from sub customer")
  }

  if (!subCustomer?.contacts) {
    return <Placeholder.Table />
  }

  return (
    <List>
      {contactFormNode}
      {passwordNode}
      {confirmBlockNode}
      {confirmDeleteUserNode}
      {addExistingContactsNode}
      <Grid columns={["auto", "max-content"]} gap={8}>
        <div>
          <TextGroupMedium heading="Contacts" />
          <Spacer size={6} />
          <div
            css={css`
              max-width: 400px;
            `}
          >
            <Fields.Search value={contactQuery} onChange={setContactQuery} />
          </div>
        </div>
        <div>
          <ButtonsWrapper>
            <CreateButton onClick={handleAddExistingContacts}>Add existing contact</CreateButton>
            <CreateButton onClick={handleCreateContact}>New contact</CreateButton>
          </ButtonsWrapper>
        </div>
      </Grid>
      <Table.HorizontalOverflow minWidth={1800}>
        <Table
          data={subCustomer.contacts.filter(
            contact =>
              contact.name.trim().toLowerCase().includes(contactQuery.trim()) ||
              contact.email.trim().toLowerCase().includes(contactQuery.trim()),
          )}
        >
          <Table.Column heading="Name">
            {row => {
              const contact = row as ICustomer["contacts"][0]
              const jobTitle = contact.job_title ? (
                <div
                  css={css`
                    white-space: nowrap;
                  `}
                >
                  <P color="#888" size="small" blockSpacing={0}>
                    {contact.job_title}
                  </P>
                </div>
              ) : null
              if (contact.sage_id) {
                return (
                  <QuickToolTip content="This contact was imported from Sage so cannot be edited here. To change any details, edit the record in Sage and Sync your data">
                    <span
                      css={css`
                        text-decoration: underline;
                        text-decoration-style: dashed;
                      `}
                    >
                      {jobTitle}
                      {contact.name}
                      <br />
                      <span
                        css={css`
                          font-size: 11px;
                          font-weight: 600;
                          color: ${themeColors.grey};
                        `}
                      >
                        Imported from Sage
                      </span>
                    </span>
                  </QuickToolTip>
                )
              } else {
                return (
                  <span
                    onClick={() => handleEditContact(contact)}
                    css={css`
                      font-weight: 600;
                      color: ${brandColors.darkMint};
                      text-decoration: underline;
                      cursor: pointer;
                    `}
                  >
                    {jobTitle}
                    {contact.name} <Icon icon={icons.edit} />
                  </span>
                )
              }
            }}
          </Table.Column>
          <Table.Column heading="Email">
            {row => (
              <React.Fragment>
                {row.email}
              </React.Fragment>
            )}
          </Table.Column>
          <Table.Column heading="MS Email/Events">
            {row => (
              <ToggleSwitch
                value={row.useForMsEmailEventFetch}
                onChange={value => handleToggleUseForMsResources(row.id, value)}
                disabled={!row.email}
              />
            )}
          </Table.Column>
          <Table.Column heading={"Marketing Accepted"}>
            {row =>
              row.user ? (
                <ToggleSwitch
                  value={row.marketingAccepted}
                  onChange={value => handleToggleMarketingAccepted(row.id, value)}
                  disabled={!row.email}
                />
              ) : (
                ""
              )
            }
          </Table.Column>
          <Table.Column
            heading={
              <QuickToolTip content="When enabled, this user will only be able to create quotes in the customer area.">
                Quotes Only
              </QuickToolTip>
            }
          >
            {row =>
              row.user ? (
                <ToggleSwitch
                  value={row.user.customerAreaQuotesOnly}
                  onChange={value => handleToggleCustomerAreaQuotesOnly(row.user.id, value)}
                  disabled={!row.user}
                />
              ) : (
                ""
              )
            }
          </Table.Column>
          <Table.Column heading="User account">
            {row => {
              const contact = row as ICustomer["contacts"][0]
              return contact.user ? (
                <Grid columns={["max-content", "max-content"]} gap={4}>
                  <DropdownMenu
                    items={[
                      {
                        children: contact.user.last_seen ? "Send Password Reset" : "Send invite",
                        onClick: () => {
                          skeem.generatePasswordResetToken({ email: contact.user.skeem_email })
                          alerts.genericSuccess(
                            contact.user.last_seen ? "Password Reset Sent" : "Invite sent",
                          )
                        },
                      },
                      {
                        children: "Change password",
                        onClick: () => handleChangeUserPassword(contact),
                      },
                      {
                        children: `${contact.user.isBlocked ? "Unblock" : "Block"} account`,
                        onClick: () => blockContact(contact),
                      },
                      {
                        children: `Delete user account`,
                        onClick: () => handleDeleteUserAccount(contact.user.id),
                      },
                    ].filter(Boolean)}
                  >
                    <IconButton icon={icons.ellipsis} buttonStyle="text" />
                  </DropdownMenu>
                  <div>
                    {contact.user.customerIsPrimaryAccountHolder && (
                      <div
                        css={css`
                          margin-bottom: 2px;
                          background-color: ${themeColors.green};
                          padding: 2px;
                          color: white;
                          text-align: center;
                        `}
                      >
                        <b>Primary User</b>
                      </div>
                    )}
                    <b>{contact.user.skeem_email}</b>
                    <br />
                    Last activity:{" "}
                    {contact.user.last_seen
                      ? differenceInSeconds(new Date(), new Date(contact.user.last_seen)) < 30
                        ? "Just now"
                        : formatDistanceToNowStrict(new Date(contact.user.last_seen), {
                            addSuffix: true,
                          })
                      : "-"}
                    {contact.user.isBlocked ? (
                      <P weight={600} color="red" blockSpacing={0} size="small">
                        Contact blocked
                      </P>
                    ) : null}
                  </div>
                </Grid>
              ) : row.email && row.name ? (
                <Button
                  buttonColor="white"
                  onClick={() => handleEditContact(contact, { focusUserSection: true })}
                >
                  Create / Assign user account
                </Button>
              ) : (
                <span>Cannot assign user to the contact</span>
              )
            }}
          </Table.Column>
          <Table.DataColumn
            field="phone"
            heading="Phone"
            render={phone => (phone ? <a href={["tel:", phone].join("")}>{phone}</a> : null)}
          />
          <Table.Column heading="# of quotes past 30 days">
            {row => {
              const contact = row as ICustomer["contacts"][0]
              return (
                <Link
                  to={buildRoute(
                    eRoutes["/sub-customers/:id/quotes"],
                    { id: subCustomer.id },
                    { qs: { contact: row.id } },
                  )}
                >
                  {
                    contact.quotes.filter(
                      quote =>
                        quote.created_at &&
                        new Date(quote.created_at) > sub(new Date(), { days: 30 }),
                    ).length
                  }{" "}
                  (View quotes)
                </Link>
              )
            }}
          </Table.Column>
          <Table.Column heading="# of orders past 30 days">
            {row => {
              const contact = row as ICustomer["contacts"][0]
              return (
                <Link
                  to={buildRoute(
                    eRoutes["/sub-customers/:id/orders"],
                    { id: subCustomer.id },
                    { qs: { contact: row.id } },
                  )}
                >
                  {
                    contact.orders.filter(
                      order =>
                        order.created_at &&
                        new Date(order.created_at) > sub(new Date(), { days: 30 }),
                    ).length
                  }{" "}
                  (View orders)
                </Link>
              )
            }}
          </Table.Column>
          <MenuColumn
            items={row => [
              {
                children: "Remove",
                onClick: () => handleRemove(row.id),
                confirmation: {
                  heading: "Are you sure you want to remove this contact from the sub customer?",
                  body: "This does not delete the contact or remove them from the parent customer.",
                },
              },
            ]}
          />
        </Table>
      </Table.HorizontalOverflow>
      <Spacer size={64} />
    </List>
  )
}

export function useContactCreateForSubOrCustomerConfirmation() {
  const alerts = useSiteAlerts()
  const skeem = window.skeem
  const events = useEvents()

  const [node, confirm] = useConfirmation<
    {
      contact: Partial<ICustomer["contacts"][0]>
      customer: IRecord<"customers", "name" | "isGenericCustomer"> & {
        branch: IRecord<"branches">
      }
      subCustomer?: IRecord<"sub_customers", "name">
      opts?: { focusUserSection?: boolean }
    },
    | (Omit<Partial<ICustomer["contacts"][0]>, "id"> & {
        createUser: boolean
        password?: string
        emailOverrideField?: string
        quickInvite?: boolean
      })
    | false
  >(({ onResolve, data }) => {
    const { quotes, orders, ...initialData } = data.contact
    const [enterManualEmail, setEnterManualEmail] = useState(false)

    return (
      <Modal
        render={() => (
          <Grid gap={12}>
            <TextGroupMedium
              heading={
                data.opts?.focusUserSection
                  ? "Assign / create user for contact"
                  : "Enter information for Contact"
              }
              body={`Contact for ${data.subCustomer ? "sub " : ""}customer: ${
                (data.subCustomer ?? data.customer).name
              }`}
            />
            <Form2.Form initialData={initialData} onSubmit={onResolve}>
              <CheckEmailForNewUserNotTaken
                onIsTaken={() => setEnterManualEmail(true)}
                isAskingForOverride={enterManualEmail}
              />
              {!data.opts?.focusUserSection && (
                <React.Fragment>
                  <Form2.Text field="job_title" />
                  <Form2.Text field="first_name" validations={{ presence: true }} />
                  <Form2.Text field="last_name" validations={{ presence: true }} />
                  <Form2.Email field="email" validations={{ presence: true }} />
                  <Form2.Phone field="phone" />
                  <QuickToolTipWrapper
                    showToolTip={!!data.customer.isGenericCustomer}
                    content={"Generic customers cannot have a default price list contact."}
                  >
                    <Form2.Checkbox
                      field="receivesMonthlyPriceList"
                      label="Receives Price List When Sent"
                      disabled={!!data.customer.isGenericCustomer}
                    />
                  </QuickToolTipWrapper>
                </React.Fragment>
              )}
              <Form2.WithField<boolean>
                field="createUser"
                render={field =>
                  field.getValue() ? (
                    <Grid gap={8}>
                      {enterManualEmail && (
                        <FieldWithDescription description="This email is already in use for another user accounts. Contacts do not need unique email addresses but user accounts do. Enter a unique email address for the contact here.">
                          <Form2.Email
                            field="emailOverrideField"
                            label="User account email (Must be unique)"
                            validations={{ presence: true }}
                          />
                        </FieldWithDescription>
                      )}
                      <Grid columns={[{ fr: 1 }, "max-content"]} alignItems="start" gap={8}>
                        <List>
                          <FloatingBox>
                            <H4>User</H4>
                            <P size="small">
                              On save, a password will be generated and a user account will be
                              created for this contact.
                            </P>
                            <Spacer />
                            <Form2.WithField<boolean>
                              field="quickInvite"
                              render={field => (
                                <Fields.Checkbox
                                  value={field.getValue()}
                                  onChange={field.setValue}
                                  label="Send invite when saving new account"
                                />
                              )}
                            />
                          </FloatingBox>
                        </List>
                        <div>
                          <CancelButton
                            onClick={() => field.setValue(false)}
                            extendTheme={{ padding: 8, fontSize: 12 }}
                          >
                            Choose existing
                          </CancelButton>
                        </div>
                      </Grid>
                    </Grid>
                  ) : (
                    <List>
                      <Form2.WithForm
                        subscribe={{ dataChange: true }}
                        render={form => (
                          <Form2.WithField<{ id: string }>
                            field="user"
                            render={userSelectField => (
                              <Grid columns={[{ fr: 1 }, "max-content"]} alignItems="end" gap={8}>
                                <UserSelectList
                                  label="Select an existing user"
                                  value={userSelectField.getValue()?.id}
                                  onChange={(id, user) => {
                                    userSelectField.setValue({ id })

                                    form.getField("first_name").setValue(user?.first_name ?? "")
                                    form.getField("last_name").setValue(user?.last_name ?? "")
                                    form.getField("email").setValue(user?.skeem_email ?? "")
                                    form.getField("phone").setValue(user?.phoneNumber ?? "")
                                    form
                                      .getField("marketingAccepted")
                                      .setValue(user?.marketingAccepted ?? false)
                                  }}
                                  customerId={data.customer.id}
                                  branchId={data.customer.branch.id ?? undefined}
                                  filter={{
                                    or: [
                                      data.contact.id && {
                                        matches: {
                                          contact: data.contact.id,
                                        },
                                      },
                                      {
                                        matches: {
                                          contact: null,
                                        },
                                      },
                                    ].filter(Boolean),
                                  }}
                                />
                                <div>
                                  <CreateButton
                                    onClick={() => field.setValue(true)}
                                    extendTheme={{ padding: 8, fontSize: 12 }}
                                  >
                                    Create new user
                                  </CreateButton>
                                </div>
                              </Grid>
                            )}
                          />
                        )}
                      />
                    </List>
                  )
                }
              />
              <Form2.Checkbox
                field="default_delivery_note"
                label="Use this contact as the default contact for delivery notes"
                disabled={data.customer.isGenericCustomer}
              />
              <Form2.Checkbox
                field="useForMsEmailEventFetch"
                label="Use this contact in Emails & Events pulled from outlook"
              />
              <Form2.Checkbox field="marketingAccepted" />
              <Grid columns={["auto", "max-content"]}>
                <ConfirmCancelButtons onConfirm="submit" onCancel={() => onResolve(false)} />
              </Grid>
            </Form2.Form>
          </Grid>
        )}
        isOpen
      />
    )
  })

  async function handleCreate(
    customer: IRecord<"customers", "name" | "isGenericCustomer"> & { branch: IRecord<"branches"> },

    subCustomer?: IRecord<"sub_customers", "name">,
  ) {
    const contact = await confirm({ customer, subCustomer, contact: {} })

    if (contact === false) {
      return
    } else {
      try {
        const { createUser, emailOverrideField, password, quickInvite, ...mutationData } = contact
        const createdContact = await skeem.mutate<{
          default_delivery_note?: boolean
          email: string
          user: { email: string }
        }>({
          contacts: {
            create: {
              ...mutationData,
              name: mutationData.first_name + " " + mutationData.last_name,
              source_branch_id: customer.branch.id,

              branch: { set: customer.branch.id },

              customer: { set: customer.id },

              sub_customers: subCustomer ? { add: subCustomer.id } : undefined,
              receivesMonthlyPriceList: !!mutationData.receivesMonthlyPriceList,
              user: createUser
                ? {
                    create: {
                      name: mutationData.first_name + " " + mutationData.last_name,
                      first_name: mutationData.first_name,
                      last_name: mutationData.last_name,
                      skeem_email: emailOverrideField ?? mutationData.email,
                      skeem_password: generatePassword(),
                      userAccountType: "external",
                      customer: { set: customer.id },

                      branch: { set: customer.branch.id },

                      phoneNumber: mutationData.phone,
                      marketingAccepted: mutationData.marketingAccepted,
                    },
                  }
                : { set: mutationData.user?.id || null },
            },
          },
        })
        events.createRecord("contacts", createdContact.id, createdContact.email)
        if (subCustomer) {
          events.associateWithInverse(
            "sub_customers",
            subCustomer.id,
            {
              association: "contacts",
              recordId: createdContact.id,
              nameAttr: "email",
            },
            "name",
          )
        }
        if (mutationData.default_delivery_note) {
          const contacts = await skeem.fetch2<IRecord<"contacts">[]>({
            model: {
              name: "contacts",
              filter: {
                and: [
                  { not: { matches: { id: createdContact.id } } },
                  {
                    matches: {
                      customer: customer.id,

                      branch: customer.branch.id,

                      default_delivery_note: true,
                    },
                  },
                ],
              },
            },
          })
          await skeem.mutate({
            contacts: contacts.map(v => ({ update: { id: v.id, default_delivery_note: false } })),
          })
        }
        if (quickInvite && createUser) {
          await skeem.generatePasswordResetToken({ email: createdContact.user.email })
          alerts.genericSuccess("Invite sent")
        }
        alerts.genericSuccess("Contact created successfully")
        return createdContact
      } catch (e) {
        console.error(e)
        if (e.message.includes("must be unique")) {
          alerts.genericError("Failed to create contact, email already in use.")
        } else {
          alerts.genericError("Failed to create contact: " + e.message)
        }
      }
    }
  }

  return [node, confirm, handleCreate] as const
}

export function CheckEmailForNewUserNotTaken({
  onIsTaken,
  isAskingForOverride,
}: {
  onIsTaken(): void
  isAskingForOverride?: boolean
}) {
  const skeem = window.skeem
  useFormSubmitLock(async formState => {
    const emailField = formState.getField("email")
    const passwordField = formState.getField("password")
    const emailOverrideField = formState.getField("emailOverrideField")
    if (!passwordField.getValue()) {
      return true
    }
    const matchingEmailsCount = await skeem.fetch2<number>({
      count: {
        name: "skeem_users",
        filter: {
          matches: { skeem_email: emailOverrideField.getValue() ?? emailField.getValue() },
        },
      },
    })
    if (isBlank(emailOverrideField.getValue()) && isAskingForOverride) {
      emailOverrideField.setError(["Please enter an email address"])
      return false
    }
    if (matchingEmailsCount > 0) {
      emailOverrideField.setError(["Email is already taken"])
      onIsTaken()
      return false
    } else {
      return true
    }
  })
  return null
}

function generatePassword() {
  const alphabet = "abcdefghijklmnopqrstuvwxyz"
  return Array(12)
    .fill(["0123456789", alphabet, alphabet.toUpperCase()].join(""))
    .map(function (x) {
      return x[Math.floor(Math.random() * x.length)]
    })
    .join("")
}

type IContact = IRecord<"contacts", "email" | "name"> & {
  sub_customers?: IRecord<"sub_customers", "name">[]
}

function useManageExistingContact({
  subCustomerId,
  customerId,
  callback,
}: {
  subCustomerId: string
  customerId: string
  callback?: () => any
}) {
  const skeem = window.skeem
  const alerts = useSiteAlerts()
  const events = useEvents()

  return useConfirmation<void, void>(({ onResolve }) => {
    const [contacts, setContacts] = useState<IContact[] | null>(null)
    async function loadContacts() {
      const contacts = await skeem.fetch2<IContact[]>({
        model: {
          name: "contacts",
          attributes: ["email", "name", { name: "sub_customers", attributes: ["name"] }],
          filter: {
            and: [
              { matches: { customer: customerId } },
              {
                eq: [
                  {
                    count: {
                      path: [{ name: "sub_customers", filter: { matches: { id: subCustomerId } } }],
                    },
                  },
                  { value: 0 },
                ],
              },
            ],
          },

          sort: { by: "name", direction: "desc" },
        },
      })
      setContacts(contacts)
    }

    useAsyncEffect(loadContacts, [subCustomerId, customerId])

    const setSubCustomer = useAsyncCallback(async (contact: IContact) => {
      await skeem.mutate({
        contacts: { update: { id: contact.id, sub_customers: { add: subCustomerId } } },
      })
      alerts.genericSuccess("Contact added to sub customer")
      events.associateWithInverse(
        "sub_customers",
        subCustomerId,
        {
          association: "contacts",
          recordId: contact.id,
          nameAttr: "email",
        },
        "name",
      )
      await loadContacts()
      callback?.()
    })

    return (
      <Modal
        render={() => (
          <div>
            <TextGroupMedium heading="Add from existing contacts" />
            <Spacer />
            {contacts && contacts.length === 0 ? (
              <NoResults />
            ) : (
              <Table data={contacts ?? []}>
                <Table.DataColumn field="email" heading="Email" />
                <Table.DataColumn field="name" heading="Name" />
                <Table.DataColumn
                  field="sub_customers"
                  heading="Sub Customers"
                  render={c => c?.map((x: { name: string }) => x.name).join(", ")}
                />
                <Table.Column>
                  {row => (
                    <Button onClick={() => setSubCustomer(row)} disabled={setSubCustomer.running}>
                      Add
                    </Button>
                  )}
                </Table.Column>
              </Table>
            )}
            <Spacer />
            <Button onClick={() => onResolve()} buttonColor="white">
              Close
            </Button>
          </div>
        )}
        isOpen
      />
    )
  })
}
